import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, XCircle, Truck, DollarSign, RefreshCw, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import AdminLayout from './AdminLayout';
import { getOrders, isAuthenticated } from '../../services/adminApi';

const statusColors = {
  NEW: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  CONFIRMED: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  PAID: 'bg-green-500/20 text-green-300 border-green-500/30',
  CANCELLED: 'bg-red-500/20 text-red-300 border-red-500/30',
  DELIVERED: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
};

const statusIcons = {
  NEW: Clock,
  CONFIRMED: CheckCircle,
  PAID: DollarSign,
  CANCELLED: XCircle,
  DELIVERED: Truck,
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [navigate, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const data = await getOrders(status);
      setOrders(data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      if (error.message.includes('401') || error.message.includes('authenticated')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const orderStats = {
    total: orders.length,
    new: orders.filter(o => o.status === 'NEW').length,
    confirmed: orders.filter(o => o.status === 'CONFIRMED').length,
    paid: orders.filter(o => o.status === 'PAID').length,
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-white">Orders</h1>
            <p className="text-stone-400 mt-1">Manage and track customer orders</p>
          </div>
          <Button
            onClick={fetchOrders}
            variant="outline"
            className="border-stone-600 text-stone-300 hover:bg-stone-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#2a2a2e] border-stone-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-stone-400 text-sm">Total Orders</p>
                  <p className="text-2xl font-semibold text-white">{orderStats.total}</p>
                </div>
                <Package className="w-8 h-8 text-stone-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2a2a2e] border-stone-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-stone-400 text-sm">New</p>
                  <p className="text-2xl font-semibold text-blue-400">{orderStats.new}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2a2a2e] border-stone-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-stone-400 text-sm">Confirmed</p>
                  <p className="text-2xl font-semibold text-yellow-400">{orderStats.confirmed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#2a2a2e] border-stone-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-stone-400 text-sm">Paid</p>
                  <p className="text-2xl font-semibold text-green-400">{orderStats.paid}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 mb-6">
          <span className="text-stone-400 text-sm">Filter by status:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 bg-stone-800 border-stone-600 text-white">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-stone-600">
              <SelectItem value="all" className="text-white hover:bg-stone-700">All Status</SelectItem>
              <SelectItem value="NEW" className="text-white hover:bg-stone-700">New</SelectItem>
              <SelectItem value="CONFIRMED" className="text-white hover:bg-stone-700">Confirmed</SelectItem>
              <SelectItem value="PAID" className="text-white hover:bg-stone-700">Paid</SelectItem>
              <SelectItem value="DELIVERED" className="text-white hover:bg-stone-700">Delivered</SelectItem>
              <SelectItem value="CANCELLED" className="text-white hover:bg-stone-700">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <Card className="bg-[#2a2a2e] border-stone-700">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-stone-500 animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-700">
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Order ID</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Date</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Customer</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Details</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Total</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Status</th>
                      <th className="text-left p-4 text-stone-400 font-medium text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const StatusIcon = statusIcons[order.status] || Package;
                      return (
                        <tr key={order.id} className="border-b border-stone-700/50 hover:bg-stone-800/50">
                          <td className="p-4">
                            <span className="text-white font-mono text-sm">
                              {order.id.slice(0, 8)}...
                            </span>
                          </td>
                          <td className="p-4 text-stone-300 text-sm">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="text-white text-sm">{order.customerName || 'Anonymous'}</p>
                              <p className="text-stone-500 text-xs">{order.customerPhone || '-'}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="text-white text-sm">
                                {order.roseVariety} × {order.quantity}
                              </p>
                              <p className="text-stone-500 text-xs">
                                {order.stemLength}cm • {order.fulfillmentMethod === 'DELIVERY' ? 'Delivery' : 'Pickup'}
                              </p>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-rose-400 font-semibold">
                              ${order.pricingBreakdown?.totalEstimated || 0}
                            </span>
                          </td>
                          <td className="p-4">
                            <Badge className={`${statusColors[order.status]} border`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {order.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                              className="text-stone-400 hover:text-white hover:bg-stone-700"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
